import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { Footer } from '@/sections/Footer';
import { FaqSection, faqSchema } from '@/components/FaqSection';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, Pill, CtaLink, Tile, Phone, BrowserFrame } from '@/components/system';
import { APPS, APP_BY_SLUG, appPath } from '@/apps/data';
import { STATUS_LABEL, type AppDef } from '@/apps/types';

/**
 * One page per app in the MEC platform. Driven entirely by apps/data.ts.
 * Apple idiom: hero statement → real product (when we have it) → what it does (bento)
 * → how it connects → FAQ → closer. Never renders placeholder/fake UI.
 */
function StatusChip({ app }: { app: AppDef }) {
  const tone =
    app.status === 'disponible' ? 'bg-brand/10 text-brand' : app.status === 'muy-pronto' ? 'bg-text-primary/5 text-text-secondary' : 'bg-text-primary/5 text-text-secondary';
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${tone}`}>{STATUS_LABEL[app.status]}</span>;
}

function Media({ app }: { app: AppDef }) {
  const m = app.media;
  if (!m) return null;
  if (m.kind === 'browser') {
    return (
      <div className="relative mt-14 sm:mt-20">
        <Container>
          <BrowserFrame src={m.src} alt={m.alt} width={m.width} height={m.height} priority />
        </Container>
      </div>
    );
  }
  const items = m.items.slice(0, 3);
  return (
    <div className="relative mt-14 sm:mt-20 h-[420px] sm:h-[520px] lg:h-[560px] overflow-hidden">
      <div className="absolute inset-x-0 top-0 flex items-start justify-center gap-5 sm:gap-8 lg:gap-10">
        {items.map((it, i) => {
          const side = items.length === 3 && i !== 1;
          return (
            <div key={it.src} className={side ? 'hidden sm:block translate-y-16 lg:translate-y-20' : ''}>
              <Phone src={it.src} alt={it.alt} width={side ? 250 : 290} priority={i === 1 || items.length === 1} />
            </div>
          );
        })}
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent via-white/70 to-bg-light" />
    </div>
  );
}

export function AppPage({ app }: { app: AppDef }) {
  const featuresRef = useReveal<HTMLElement>();
  const galleryRef = useReveal<HTMLElement>();
  const connRef = useReveal<HTMLElement>();
  const closerRef = useReveal<HTMLElement>();
  const others = APPS.filter((a) => a.slug !== app.slug);

  return (
    <main className="bg-bg-light">
      <SEO
        title={app.seo.title}
        description={app.seo.description}
        path={appPath(app.slug)}
        keywords={app.seo.keywords}
        jsonLd={app.faqs ? faqSchema(app.faqs) : undefined}
      />

      {/* Hero */}
      <section className={`relative w-full bg-white overflow-hidden pt-[120px] sm:pt-[140px] ${app.media ? 'pb-0' : 'pb-16 sm:pb-20'}`}>
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <img src={app.logo} alt="" width={36} height={36} className="w-9 h-9 rounded-xl" />
              <span className="font-semibold text-text-primary">{app.name}</span>
              <StatusChip app={app} />
            </div>
            <Display as="h1" size="xl">{app.hero.headline}</Display>
            <Lead className="mt-6 mx-auto max-w-2xl">{app.hero.lead}</Lead>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <Pill to={app.closer.primary.to}>{app.closer.primary.label}</Pill>
              {app.closer.secondary && <CtaLink to={app.closer.secondary.to}>{app.closer.secondary.label}</CtaLink>}
            </div>
            <p className="mt-6 text-sm text-text-tertiary">{app.statusNote}</p>
          </div>
        </Container>
        <Media app={app} />
      </section>

      {/* What it does */}
      <Section tone="grey" ref={featuresRef} className="py-16 sm:py-20 lg:py-24">
        <Container>
          <div className="max-w-3xl">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Qué hace</p>
            <Display data-reveal size="md">{app.tagline}</Display>
            <Lead data-reveal className="mt-5">Para {app.audience.charAt(0).toLowerCase() + app.audience.slice(1)}</Lead>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {app.features.map((f) => (
              <Tile key={f.title} data-reveal className="p-7 sm:p-8">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{f.title}</h3>
                <p className="mt-2 text-text-secondary leading-relaxed">{f.text}</p>
              </Tile>
            ))}
          </div>
        </Container>
      </Section>

      {/* Close-ups — real screenshots only */}
      {app.gallery && (
        <Section tone="grey" ref={galleryRef} className="pt-0 pb-16 sm:pb-20">
          <Container>
            <div className="grid md:grid-cols-2 gap-5">
              {app.gallery.map((g) => (
                <figure key={g.src} data-reveal className={g.span === 2 ? 'md:col-span-2' : ''}>
                  {g.frame === 'browser' ? (
                    <BrowserFrame src={g.src} alt={g.alt} width={g.width} height={g.height} />
                  ) : (
                    <div className="rounded-[20px] bg-white ring-1 ring-black/10 overflow-hidden max-w-md mx-auto">
                      <img src={g.src} alt={g.alt} width={g.width} height={g.height} loading="lazy" decoding="async" className="w-full h-auto block" />
                    </div>
                  )}
                  <figcaption className="mt-3 text-center text-sm text-text-secondary">{g.caption}</figcaption>
                </figure>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* How it connects */}
      <Section tone="white" ref={connRef} className="py-16 sm:py-20 lg:py-24">
        <Container>
          <div className="max-w-3xl">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Cómo encaja</p>
            <Display data-reveal size="md">Una plataforma. Cada app hace su parte.</Display>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {app.connections.map((c) => {
              const other = APP_BY_SLUG[c.slug];
              return (
                <Link key={c.slug} to={appPath(c.slug)} data-reveal className="group rounded-[28px] bg-bg-light p-8 flex gap-5 items-start hover:bg-line/60 transition-colors">
                  <img src={other.logo} alt="" width={48} height={48} loading="lazy" className="w-12 h-12 rounded-2xl shrink-0" />
                  <div>
                    <p className="font-semibold text-text-primary flex items-center gap-1">
                      {other.name}
                      <ChevronRight className="w-4 h-4 text-text-tertiary transition-transform group-hover:translate-x-0.5" />
                    </p>
                    <p className="mt-1 text-text-secondary leading-relaxed">{c.text}</p>
                  </div>
                </Link>
              );
            })}
          </div>
          <div data-reveal className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
            <span className="text-text-tertiary">Toda la plataforma:</span>
            {others.map((o) => (
              <Link key={o.slug} to={appPath(o.slug)} className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary">
                <img src={o.logo} alt="" width={20} height={20} loading="lazy" className="w-5 h-5 rounded-md" />
                {o.name}
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      {app.faqs && (
        <Section tone="grey" className="py-0 pt-16 sm:pt-20">
          <FaqSection faqs={app.faqs} />
        </Section>
      )}

      {/* Closer */}
      <Section tone="grey" ref={closerRef} className="pt-0 pb-16 sm:pb-20">
        <Container>
          <div className="rounded-[32px] bg-white px-8 py-16 sm:px-12 sm:py-20 text-center">
            <Display data-reveal size="md">{app.closer.headline}</Display>
            <Lead data-reveal className="mt-5 mx-auto max-w-2xl">{app.closer.text}</Lead>
            <div data-reveal className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <Pill to={app.closer.primary.to}>{app.closer.primary.label}</Pill>
              {app.closer.secondary && <CtaLink to={app.closer.secondary.to}>{app.closer.secondary.label}</CtaLink>}
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </main>
  );
}
